import { useState } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faDollarSign,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";

// ─── Cash Book View Component ──────────────────────────────────────────────

const CashBookView = ({
  transactions,
  formatCurrency,
  formatDate,
  getCategoryColor,
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  typeFilter,
  setTypeFilter,
  categories,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  totalItems,
  totalPages,
  currentData,
  startIndex,
}) => {
  const [showTable, setShowTable] = useState(false);

  // Calculate running balance for all transactions (for totals)
  const getAllTransactionsWithBalance = () => {
    let balance = 0;
    // Sort by date descending (newest first)
    const sortedTransactions = [...transactions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    return sortedTransactions.map((t) => {
      if (t.type === "IN") {
        balance += t.amount;
      } else {
        balance -= t.amount;
      }
      return { ...t, runningBalance: balance };
    });
  };

  const allTransactionsWithBalance = getAllTransactionsWithBalance();

  // Calculate totals from all transactions
  const totalCashIn = transactions
    .filter((t) => t.type === "IN")
    .reduce((sum, t) => sum + t.amount, 0);

  const totalCashOut = transactions
    .filter((t) => t.type === "OUT")
    .reduce((sum, t) => sum + t.amount, 0);

  const closingBalance = totalCashIn - totalCashOut;

  // Get current page data with running balance
  const getCurrentPageWithBalance = () => {
    const allWithBalance = allTransactionsWithBalance;

    // Find the balance before the current page
    const beforeCurrentPage = allWithBalance.slice(0, startIndex);
    const balanceBefore =
      beforeCurrentPage.length > 0
        ? beforeCurrentPage[beforeCurrentPage.length - 1].runningBalance
        : 0;

    // Get current page data and map with running balance
    const currentPageData = currentData.map((t) => {
      const found = allWithBalance.find((item) => item.id === t.id);
      return { ...t, runningBalance: found ? found.runningBalance : 0 };
    });

    return { currentPageData, balanceBefore };
  };

  const { currentPageData, balanceBefore } = getCurrentPageWithBalance();

  // Summary by category with colors
  const summaryByCategory = transactions.reduce((acc, t) => {
    if (!acc[t.category]) {
      acc[t.category] = { in: 0, out: 0 };
    }
    if (t.type === "IN") {
      acc[t.category].in += t.amount;
    } else {
      acc[t.category].out += t.amount;
    }
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Cash Book Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 p-4">
          <p className="text-xs uppercase tracking-wider text-green-700 font-semibold">
            Total Cash IN
          </p>
          <p className="text-xl font-bold text-green-800">
            {formatCurrency(totalCashIn)}
          </p>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 p-4">
          <p className="text-xs uppercase tracking-wider text-red-700 font-semibold">
            Total Cash OUT
          </p>
          <p className="text-xl font-bold text-red-800">
            {formatCurrency(totalCashOut)}
          </p>
        </Card>
        <Card
          className={`bg-gradient-to-br p-4 ${closingBalance >= 0 ? "from-blue-50 to-blue-100 border-blue-200" : "from-red-50 to-red-100 border-red-200"}`}
        >
          <p className="text-xs uppercase tracking-wider font-semibold">
            Closing Balance
          </p>
          <p
            className={`text-xl font-bold ${closingBalance >= 0 ? "text-blue-800" : "text-red-800"}`}
          >
            {formatCurrency(closingBalance)}
          </p>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 p-4">
          <p className="text-xs uppercase tracking-wider text-purple-700 font-semibold">
            Total Entries
          </p>
          <p className="text-xl font-bold text-purple-800">
            {transactions.length}
          </p>
        </Card>
      </div>

      {/* Category Summary with Colors */}
      <Card className="bg-white border border-gray-200 p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <FontAwesomeIcon
            icon={faChartPie}
            className="text-blue-600 h-4 w-4"
          />
          Cash flow by category
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {Object.entries(summaryByCategory).map(([category, amounts]) => (
            <div
              key={category}
              className={`flex items-center justify-between p-2 rounded-sm border px-3 ${getCategoryColor(category)}`}
            >
              <span className="text-sm font-medium truncate text-white">
                {category}
              </span>
              <div className="flex items-center gap-1 text-sm">
                {amounts.in > 0 && (
                  <span className="text-white font-semibold">
                    +{formatCurrency(amounts.in)}
                  </span>
                )}
                {amounts.out > 0 && amounts.in > 0 && (
                  <span className="text-white/30 mx-0.5">|</span>
                )}
                {amounts.out > 0 && (
                  <span className="text-white font-semibold">
                    -{formatCurrency(amounts.out)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* View More Button */}
      <div className="flex justify-center">
        <Button
          onClick={() => setShowTable(!showTable)}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white px-6"
        >
          <FontAwesomeIcon icon={showTable ? faChevronUp : faChevronDown} />
          {showTable ? "Hide details" : "View more"}
        </Button>
      </div>

      {/* Table - Hidden until View More is clicked */}
      {showTable && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by description or category..."
              className="flex-1"
            />

            <div className="flex flex-wrap items-center gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-40 h-9 border border-gray-300 rounded-lg bg-white text-sm">
                  <div className="flex items-center gap-2">
                    <SelectValue placeholder="All Categories" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-32 h-9 border border-gray-300 rounded-lg bg-white text-sm">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="IN">Money IN</SelectItem>
                  <SelectItem value="OUT">Money OUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cash Book Table */}
          <TableWrapper
            title="entries"
            totalItems={totalItems}
            currentPage={currentPage}
            totalPages={totalPages}
            className="bg-white border border-gray-200 shadow-sm"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    #
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Description
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 hidden md:table-cell">
                    Category
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Cash IN
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Cash OUT
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {currentPageData.length > 0 ? (
                  <>
                    {/* Show opening balance row */}
                    <tr className="bg-blue-50/50">
                      <td
                        colSpan={6}
                        className="py-2 px-4 text-right text-xs font-semibold text-gray-600"
                      >
                        Opening Balance
                      </td>
                      <td className="py-2 px-4 text-right text-xs font-semibold text-blue-600">
                        {formatCurrency(balanceBefore)}
                      </td>
                    </tr>

                    {currentPageData.map((item, i) => {
                      const globalIndex = startIndex + i + 1;
                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-blue-50/50 transition-colors"
                        >
                          <td className="py-3 px-4 text-xs text-gray-600 font-medium">
                            {globalIndex}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {formatDate(item.date)}
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-800 text-sm">
                            {item.description}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 hidden md:table-cell">
                            <Badge
                              variant="outline"
                              className={`${getCategoryColor(item.category)} text-xs font-normal px-2 py-1 rounded-full text-white`}
                            >
                              {item.category}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-sm text-green-600">
                            {item.type === "IN"
                              ? formatCurrency(item.amount)
                              : "-"}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-sm text-red-600">
                            {item.type === "OUT"
                              ? formatCurrency(item.amount)
                              : "-"}
                          </td>
                          <td
                            className={`py-3 px-4 text-right font-semibold text-sm ${item.runningBalance >= 0 ? "text-blue-600" : "text-red-600"}`}
                          >
                            {formatCurrency(item.runningBalance)}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Footer with totals */}
                    {currentPageData.length > 0 && (
                      <tr className="bg-gray-100 border-t-2 border-gray-300">
                        <td
                          colSpan={4}
                          className="py-3 px-4 text-right text-sm font-bold text-gray-700"
                        >
                          Page Totals
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-sm text-green-700">
                          {formatCurrency(
                            currentPageData
                              .filter((t) => t.type === "IN")
                              .reduce((sum, t) => sum + t.amount, 0),
                          )}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-sm text-red-700">
                          {formatCurrency(
                            currentPageData
                              .filter((t) => t.type === "OUT")
                              .reduce((sum, t) => sum + t.amount, 0),
                          )}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-sm text-blue-700">
                          {formatCurrency(
                            currentPageData.length > 0
                              ? currentPageData[currentPageData.length - 1]
                                  .runningBalance
                              : 0,
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Grand totals row */}
                    <tr className="bg-blue-50 border-t-2 border-blue-200 font-bold">
                      <td
                        colSpan={4}
                        className="py-3 px-4 text-right text-sm font-bold text-gray-800"
                      >
                        <span className="flex items-center justify-end gap-2">
                          <FontAwesomeIcon
                            icon={faDollarSign}
                            className="text-blue-600 h-3 w-3"
                          />
                          OVERALL TOTALS
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-sm text-green-700">
                        {formatCurrency(totalCashIn)}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-sm text-red-700">
                        {formatCurrency(totalCashOut)}
                      </td>
                      <td
                        className={`py-3 px-4 text-right font-bold text-sm ${closingBalance >= 0 ? "text-blue-700" : "text-red-700"}`}
                      >
                        {formatCurrency(closingBalance)}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <EmptyState
                        title="No transactions found"
                        message={
                          searchTerm ||
                          categoryFilter !== "all" ||
                          typeFilter !== "all"
                            ? "Try adjusting your search or filters"
                            : "Add your first transaction to get started"
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableWrapper>

          {/* Pagination */}
          {totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CashBookView;
